import Loader from "react-loader-spinner";
import { useCallback, useRef, useState, useEffect, Fragment } from 'react';
import { Button } from 'react-bootstrap'
import { ReactDataContext } from '@themost/react';
import Institution, { fallbackInstitution, fallbackInstitution as institutionDefaultState } from '../UI/Institution';
import InstitutionalCoordinatorContact, { fallbackCoordinator, fallbackCoordinator as institutionalCoordinatorContactDefaultState } from "../UI/InstitutionalCoordinatorContact";
import Deadline, { deadlineDefaultState } from "../UI/Deadline";
import Information, { informationEntryDefaultState } from "../UI/Information";
import Title from "../UI/Title";
import UsefulLinks from "../UI/UsefulLinks";
import AdditionalInformation from "../UI/AdditionalInformation";
import Accessibilities from "../UI/Accessibilities";
import { Redirect } from 'react-router-dom';
import { getFactsheetEndpoint } from "components/Common/CommonFunctions";

const additionalRequirementObjectDefaultState = {
    name: null,
    description: null,
    urls: [
        {
            value: null,
            language: null
        }
    ]
}

const FactsheetViewEC = ({
    match,
    numberingType = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R'],
    subnumberingType = ['1'],
    editable = false,
    title
}) => {
    const [factsheet, setFactsheet] = useState(null);
    const [error, setError] = useState(false);
    const [ewpFetchError, setEwpFetchError] = useState(false);
    const [ewpFetchLoading, setFetchEwpLoading] = useState(false);
    const [ewpFetchSuccess, setEwpFetchSuccess] = useState(false);

    const context = new ReactDataContext(process.env.REACT_APP_OPEN_API_SERVER);
    // context.setBearerAuthorization(userService.getToken());
    const params = match.params

    useEffect(() => {
        document.title = title;
    }, [])

    const handleSingleArithmeticValueChange = useCallback((propertyName) => {
        return (e) => {
            if (!editable)
                return;

            if (/[a-zA-Z]/g.test(e.target.innerText) === true) {
                setFactsheet((prevState) => {
                    return {
                        ...factsheet,
                        [`${propertyName}`]: prevState[`${propertyName}`] === null ? 0 : null
                    }
                })
                return;
            }

            if (isNaN(parseInt(e.target.innerText))) {
                setFactsheet((prevState) => {
                    return {
                        ...factsheet,
                        [`${propertyName}`]: prevState[`${propertyName}`] === null ? 0 : null
                    }
                })
                return;
            }

            setFactsheet({
                ...factsheet,
                [`${propertyName}`]: parseInt(e.target.innerText)
            })
        }
    }, [factsheet]);

    const handleSingleValueChange = (key, newValue) => {
        setFactsheet({
            ...factsheet,
            [`${key}`]: newValue
        })
    }

    const handleObjectValueChange = useCallback((propertyName, defaultState) => {
        return (key, newValue) => {
            setFactsheet((prevState) => {
                return {
                    ...factsheet, // TODO spread prevState instead of factsheet
                    [`${propertyName}`]: {
                        ...(
                            prevState?.[`${propertyName}`]
                                ? prevState[`${propertyName}`]
                                : defaultState
                        ),
                        [`${key}`]: newValue
                    }
                }
            })
        }
    }, [factsheet]);

    const handleArrayOfObjectsValueChange = (propertyName, objectDefaultState, index) => {
        return (key, newValue) => {
            setFactsheet((prevState) => {
                const keyPreviousState = prevState[`${propertyName}`][index];
                const newArray = Array.from(prevState[`${propertyName}`]);
                newArray.splice(index, 1, { ...objectDefaultState, ...keyPreviousState, [`${key}`]: newValue })

                return {
                    ...factsheet, // TODO spread prevState instead of factsheet
                    [`${propertyName}`]: prevState[`${propertyName}`].length === 0
                        ? [{ ...objectDefaultState, ...keyPreviousState, [`${key}`]: newValue }]
                        : [...newArray]
                }
            })
        }
    };

    useEffect(() => {

        (async () => {
            try {
                const factsheetResult = await fetch(process.env.REACT_APP_OPEN_API_SERVER + 'MobilityFactsheets?ec=' + params.ec);
                const data = await factsheetResult.json();
                console.log(data);
                if (factsheetResult.status != 200 || data.value.length === 0) {
                    setError(true);
                    return;
                }

                setFactsheet(data.value[0]);
            } catch (err) {
                setError(true);
                console.log(err);
            }
        })()
    }, [])

    if (error) {
        return (
            <div className="py-5 print-container">
                <div className="container">
                    <div className="row justify-content-around mb-2">
                        <div className="col-md-8">
                            <h1 className="page-header mb-0" align="center">No Mobility Factsheet found with Erasmus Code: {params.ec}</h1>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (factsheet === null) {
        return (
            <Loader
                className="spinner mx-auto text-center"
                type="TailSpin"
                color="#3A435E"
                height={70}
                width={70}
            />
        );
    }

    return (
        <div className="py-5 print-container">
            <div className="container">
                <div className="row justify-content-around mb-2">
                    <div className="col-md-8">
                        <h1 className="page-header mb-0" align="center">Mobility Factsheet with Erasmus Code: {params.ec}</h1>
                    </div>
                </div>

                <div>
                    <h3>
                        {`${numberingType[0]}. Institution`}
                    </h3>
                </div>
                <div className="table-responsive">
                    <table className="table cf" style={{ tableLayout: 'fixed' }}>
                        <thead className="cf" style={{ backgroundColor: "#3A435E", color: "white" }}>
                            <tr>
                                <th scope="col" className="text-center col-md-2">Name</th>
                                <th scope="col" className="text-center col-md-2">Erasmus code</th>
                                <th scope="col" className="text-center col-md-2">HEI Id</th>
                                <th scope="col" className="text-center col-md-2">City</th>
                                <th scope="col" className="text-center col-md-2">Country</th>
                                <th scope="col" className="text-center col-md-2">Local</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                {
                                    Object.keys(factsheet.institution ? factsheet.institution : fallbackInstitution).map(key => key !== 'id' &&
                                        <td
                                            key={key}
                                            className="text-center"
                                            style={{ wordWrap: 'break-word' }}
                                        // contentEditable={editable}
                                        // onBlur={e => {
                                        //     handleTdChange(key, e.target.innerText)
                                        // }}
                                        >
                                            {
                                                key === 'isLocal'
                                                    ? ((factsheet.institution ? factsheet.institution : fallbackInstitution)[key] === true ? 'Yes' : ((factsheet.institution ? factsheet.institution : fallbackInstitution)[key] === false ? 'No' : '-'))
                                                    : ((factsheet.institution.hasOwnProperty(key) ? factsheet.institution : fallbackInstitution)[key] ?? '-')
                                            }
                                        </td>)
                                }
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* <Institution
                    institution={factsheet.institution}
                    number={numberingType[0]}
                    title={'Institution'}
                    // userService={userService}
                    editable={editable}
                    handleTdChange={handleObjectValueChange('institution', institutionDefaultState)}
                /> */}

                {/* <InstitutionalCoordinatorContact
                    coordinator={factsheet.institutionalCoordinatorContact}
                    number={numberingType[1]}
                    title={'Institutional Coordinator Contact'}
                    subnumberingType={subnumberingType}
                    // userService={userService}
                    editable={editable}
                    handleTdChange={handleObjectValueChange('institutionalCoordinatorContact', institutionalCoordinatorContactDefaultState)}
                /> */}

                <div>
                    <h3>
                        {`${numberingType[1]}. Institutional Coordinator Contact`}
                    </h3>
                </div>
                <div className="table-responsive">
                    <table className="table cf" style={{ tableLayout: 'fixed' }}>
                        <thead className="cf" style={{ backgroundColor: "#3A435E", color: "white" }}>
                            <tr className="customTable">
                                <th scope="col" className="text-center col-md-1 align-middle">APM</th>
                                <th scope="col" className="text-center col-md-1 align-middle">Family Name</th>
                                <th scope="col" className="text-center col-md-1 align-middle">Given Name</th>
                                <th scope="col" className="text-center col-md-1 align-middle">Title</th>
                                <th scope="col" className="text-center col-md-2 align-middle">Email</th>
                                <th scope="col" className="text-center col-md-2 align-middle">Address</th>
                                <th scope="col" className="text-center col-md-2 align-middle">URL</th>
                                <th scope="col" className="text-center col-md-2 align-middle">Phone</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                {

                                    Object.keys(factsheet.institutionalCoordinatorContact ? factsheet.institutionalCoordinatorContact : fallbackCoordinator).map(key =>
                                        key !== 'id' &&
                                        key !== 'department' &&
                                        key !== 'roles' &&
                                        key !== 'uid' &&
                                        <td
                                            key={key}
                                            className="text-center"
                                            style={{ wordWrap: 'break-word' }}
                                            //contentEditable={editable}
                                            onBlur={e => {
                                                handleObjectValueChange('institutionalCoordinatorContact', institutionalCoordinatorContactDefaultState)
                                            }}
                                        >
                                            {factsheet.institutionalCoordinatorContact ? (factsheet.institutionalCoordinatorContact[key] ?? '-') : '-'}
                                        </td>
                                    )
                                }
                            </tr>
                        </tbody>
                    </table>
                </div>

                {factsheet.institutionalCoordinatorContact && <><div>
                    <h4>
                        {`${numberingType[1]}. Institutional Coordinator's Department`}
                    </h4>
                </div>
                    <div className="table-responsive">
                        <table className="table cf" style={{ tableLayout: 'fixed' }}>
                            <thead className="cf" style={{ backgroundColor: "#3A435E", color: "white" }}>
                                <tr className="customTable">
                                    <th scope="col" className="text-center align-middle col-md-6" colSpan="6">Institution Information</th>
                                    <th scope="col" className="text-center align-middle col-md-2" rowSpan="2">Name</th>
                                    <th scope="col" className="text-center align-middle col-md-2" rowSpan="2">Domain</th>
                                    <th scope="col" className="text-center align-middle col-md-2" rowSpan="2">URL</th>
                                </tr>
                                <tr style={{ backgroundColor: "#3A435E", color: "white" }}>
                                    <th className="text-center align-middle">Name</th>
                                    <th className="text-center align-middle">Erasmus Code</th>
                                    <th className="text-center align-middle">HEI Id</th>
                                    <th className="text-center align-middle">City</th>
                                    <th className="text-center align-middle">Local</th>
                                    <th className="text-center align-middle">Country</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    {
                                        factsheet.institutionalCoordinatorContact?.department ? Object.keys(factsheet.institutionalCoordinatorContact?.department?.institution).map(key =>
                                            key !== 'id' &&
                                            <td
                                                key={key}
                                                className="text-center"
                                                style={{ wordWrap: 'break-word' }}
                                            //contentEditable={editable}
                                            // onBlur={e => {
                                            //     handleTdChange('department', {...fallbackDepartment, ...department, institution: { ...institution, [`${key}`]: e.target.innerText }})
                                            // }}
                                            >
                                                {
                                                    key !== 'country' &&
                                                    (
                                                        key === 'isLocal' ?
                                                            (
                                                                factsheet.institutionalCoordinatorContact?.department?.institution[key] === true ? 'Yes'
                                                                    : (factsheet.institutionalCoordinatorContact?.department?.institution[key] === false ? 'No' : '-')
                                                            )
                                                            : (factsheet.institutionalCoordinatorContact?.department?.institution[key] ?? '-')
                                                    )
                                                }
                                            </td>
                                        ) : ["-", "-", "-", "-", "-", "-"].map(key => <td className="text-center" style={{ wordWrap: 'break-word' }}>-</td>)


                                    }
                                    <td
                                        className="text-center"
                                        style={{ wordWrap: 'break-word' }}
                                    // contentEditable={editable}
                                    // onBlur={e => {
                                    //     handleTdChange('department', {...fallbackDepartment, ...department, name: e.target.innerText})
                                    // }}
                                    >
                                        {factsheet.institutionalCoordinatorContact?.department?.institution?.name ?? '-'}
                                    </td>
                                    <td
                                        className="text-center"
                                        style={{ wordWrap: 'break-word' }}
                                    // contentEditable={editable}
                                    // onBlur={e => {
                                    //     handleTdChange('department', {...fallbackDepartment, ...department, domain: e.target.innerText})
                                    // }}
                                    >
                                        {factsheet.institutionalCoordinatorContact?.department?.institution?.domain ?? '-'}
                                    </td>
                                    <td
                                        className="text-center"
                                        style={{ wordWrap: 'break-word' }}
                                    // contentEditable={editable}
                                    // onBlur={e => {
                                    //     handleTdChange('department', {...fallbackDepartment, ...department, url: e.target.innerText})
                                    // }}
                                    >
                                        {factsheet.institutionalCoordinatorContact?.department?.institution?.url ?? '-'}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div></>}

                <Deadline
                    number={numberingType[2]}
                    title='Deadline for applications of Incoming students'
                    calendarEntry={factsheet.deadlineIncomingsCalendar}
                    editable={editable}
                    handleTdChange={handleObjectValueChange('deadlineIncomingsCalendar', deadlineDefaultState)}
                />

                <Deadline
                    number={numberingType[3]}
                    title='Deadline for Nominations'
                    calendarEntry={factsheet.deadlineNominationsCalendar}
                    editable={editable}
                    handleTdChange={handleObjectValueChange('deadlineNominationsCalendar', deadlineDefaultState)}
                />

                <Title
                    number={numberingType[4]}
                    title='Information for applications of incoming students'
                />
                <Information
                    informationEntry={factsheet.applicationIncomingsInformationEntry}
                    editable={editable}
                    handleTdChange={handleObjectValueChange('applicationIncomingsInformationEntry', informationEntryDefaultState)}
                />

                <Title
                    number={numberingType[5]}
                    title='Additional Requirements'
                />
                {
                    factsheet.additionalRequirements.length !== 0 && Array.isArray(factsheet.additionalRequirements)
                        ?
                        factsheet.additionalRequirements.map((requirement, idx) =>
                            <Fragment key={idx}>
                                {idx !== 0 ? <hr className="border"></hr> : <></>}
                                <Information
                                    titles={['Name', 'Description', 'URL', 'Language']}
                                    informationEntry={requirement}
                                    editable={editable}
                                    handleTdChange={handleArrayOfObjectsValueChange('additionalRequirements', additionalRequirementObjectDefaultState, idx)}
                                />
                            </Fragment>
                        )
                        : (!editable && <Information
                            titles={['Name', 'Description', 'URL', 'Language']}
                            informationEntry={null}
                            editable={editable}
                            handleTdChange={handleArrayOfObjectsValueChange('additionalRequirements', additionalRequirementObjectDefaultState, 0)}
                        />)
                }

                <Title
                    inline
                    number={numberingType[6]}
                    title='Weeks limit for decision response'
                />
                <h3
                    className="d-inline-block mx-2 align-middle"
                    contentEditable={editable}
                    onBlur={handleSingleArithmeticValueChange('decisionResponseWeeksLimit')}
                >
                    {factsheet.decisionResponseWeeksLimit ?? '-'}
                </h3>

                <div></div>

                <Title
                    inline
                    number={numberingType[7]}
                    title='Weeks limit for transcript of records'
                />
                <h3
                    className="d-inline-block mx-2 align-middle"
                    contentEditable={editable}
                    onBlur={handleSingleArithmeticValueChange('transcriptOfRecordsWeeksLimit')}
                >
                    {factsheet.transcriptOfRecordsWeeksLimit ?? '-'}
                </h3>

                <Title
                    number={numberingType[8]}
                    title='Accessibilities'
                />
                <Accessibilities accessibilities={factsheet.accessibilities} editable={editable} />

                <Title
                    number={numberingType[9]}
                    title='Housing Information'
                />
                <Information
                    informationEntry={factsheet.housingInformationEntry}
                    editable={editable}
                    handleTdChange={handleObjectValueChange('housingInformationEntry', informationEntryDefaultState)}
                />

                <Title
                    number={numberingType[10]}
                    title='Visa Information'
                />
                <Information
                    informationEntry={factsheet.visaInformationEntry}
                    editable={editable}
                    handleTdChange={handleObjectValueChange('visaInformationEntry', informationEntryDefaultState)}
                />

                <Title
                    number={numberingType[11]}
                    title='Insurance Information'
                />
                <Information
                    informationEntry={factsheet.insuranceInformationEntry}
                    editable={editable}
                    handleTdChange={handleObjectValueChange('insuranceInformationEntry', informationEntryDefaultState)}
                />

                <Title
                    number={numberingType[12]}
                    title='Additional Information'
                />

                <AdditionalInformation infos={factsheet.additionalInfos} editable={editable} />

                <Title
                    inline={factsheet.terminationOfAgreement ? false : true}
                    number={numberingType[15]}
                    title='Termination of Agreement'
                />
                <p
                    className={factsheet.terminationOfAgreement || editable ? 'mw-100' : 'd-inline-block mx-2 align-middle font-weight-bold'}
                    contentEditable={editable}
                    onBlur={e => setFactsheet({
                        ...factsheet,
                        terminationOfAgreement: e.target.innerText
                    })
                    }
                >
                    {factsheet.terminationOfAgreement ?? '-'}
                </p>

                {factsheet.terminationOfAgreement ? <></> : <div></div>}


                <Title
                    inline={factsheet.gradingSystem ? false : true}
                    number={numberingType[16]}
                    title='Grading System'
                />
                <p
                    className={factsheet.gradingSystem || editable ? 'mw-100' : 'd-inline-block mx-2 align-middle font-weight-bold'}
                    contentEditable={editable}
                    onBlur={e => setFactsheet({
                        ...factsheet,
                        gradingSystem: e.target.innerText
                    })
                    }
                >
                    {factsheet.gradingSystem ?? '-'}
                </p>

                <Title
                    number={numberingType[17]}
                    title='Useful Links'
                />
                <UsefulLinks
                    generalUrl={factsheet.generalUrl}
                    facultyUrl={factsheet.facultyUrl}
                    courseCatalogueUrl={factsheet.courseCatalogueUrl}
                    editable={editable}
                    handleValueChange={handleSingleValueChange}
                />

            </div>
        </div>
    );
}

export default FactsheetViewEC;